import leadLightIcon from "assets/images/account-page/lead-auto-light-img.svg";
import propStreamConnect from "assets/images/propstream-connect.png";
import connectLogo from "assets/images/connect.png";
import { getConnectLabel, isPropStream } from "utils/brand";
import propStreamConnectVisual from "assets/images/propstream-connect-visual.png";
import phoneIcon from "assets/images/phone.svg";
import searchConnectIcon from "assets/images/seatch-connect.svg";
import settingsIcon from "assets/images/settings.svg";
import stampIcon from "assets/images/stamp.svg";
import SVG from "components/base/SVG";
import Tooltip from "app/components/Tooltip";

import classNames from "classnames";
import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import css from "../style.scss";
import { CircularProgress } from "./UsageSections";
import {
  selectBillingDashboard,
  selectUsageSection,
  selectCallCountForToday,
  selectCallCountForTodayLoading,
  selectLeadAutomator,
  selectPlanDetails,
  selectUserId,
  selectUserPlan,
  isLegacyAccountPlan,
  selectCallSettingsLoading,
  selectIsLeadAutomatorLoading,
  selectListManagementEnabled,
  selectCallSettings,
  selectProfile
} from "data/user/selectors";
import { connect, useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import ErrorDisplay from "../../components/ErrorDisplay";

import numberFormat from "utils/number/format";

import config from "config";
import {
  loadLeadAutomatorSuccess,
  updateTrial14LeadAutomator,
  revertUnsubscriptionLeadAutomator,
} from "../../../data/user";
import { useToast } from "../../components/ToastMessage/ToastManager";
import LeaveMessagePopup from "../modals/LeaveMessage";
import { refreshSubscriptionCards } from "../refreshSubscriptionCards";
import { useInvestorOutreachEntitlement } from "./useInvestorOutreachEntitlement";
import LoadablePanel from "components/hoc/loadable/Panel";
import { MAX_DAILY_CALLS } from "../../../data/user/constants";

const {
  constants: {
    API: { PRICING_UPDATE_URL },
  },
} = config;

const STATUS_MAP = {
  TRIAL: "TRIAL",
  TRIAL_CANCELLED: "TRIAL_CANCELLED",
  UNSUBSCRIBED: "UNSUBSCRIBED",
  ACTIVE: "ACTIVE"
}

/**
 * Helper function to get plan name from plan details
 */
export const getPlanNameFromDetails = (planDetails) => {
  if (
    planDetails?.accountEnrolledPlans &&
    planDetails?.accountEnrolledPlans.length > 0
  ) {
    const selectedPlan = planDetails.availableUserPlans.find(
      (plan) => plan.id === planDetails?.accountEnrolledPlans[0]?.planId,
    );
    return selectedPlan?.name;
  }
  return "";
};

/**
 * Navigates to Lead Automator upgrade page with trial eligibility checks
 * Uses the same logic as LeadAutomator component's goToLeadAutomator function
 * @param {Object} params - Function parameters
 * @param {Object} params.planDetails - Plan details from Redux state
 * @param {boolean} params.isLegacyAccount - Whether user is on legacy account
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.history - React Router history object (or navigation function)
 * @param {Function} params.leaveMessagePopup - Leave message popup function from connect
 */
export const goToLeadAutomatorUpgrade = ({
  planDetails,
  isLegacyAccount,
  dispatch,
  history,
  leaveMessagePopup,
  onSuccess,
  onCancel,
}) => {
  const planName = getPlanNameFromDetails(planDetails);

  // Only Essentials Monthly plans get this trial offer
  // Users who already used the Lead Automator trial don't get it again
  // Users on an account trial don't get it (they need to activate their subscription first)
  if (
    !planDetails?.usedLATrial &&
    !planDetails?.trialAvailable &&
    planName?.includes("Essentials Monthly")
  ) {
    onSuccess && onSuccess();
    dispatch(updateTrial14LeadAutomator(true));
  }

  if (isLegacyAccount) {
    leaveMessagePopup({
      title: "Upgrade Your Account",
      message:
        "Upgrade your account to Essentials, Pro, or Elite by clicking Upgrade Plan to continue.",
      autoWidth: false,
      yesLable: "Upgrade Plan",
      cancelLable: "Cancel",
      CancelRedirect: () => onCancel && onCancel(),
      yesRedirect: () => {
        onSuccess && onSuccess();
        history.push(`/accountnew/plan`);
      },
    });
  } else {
    onSuccess && onSuccess();
    history.push(`/accountnew/leadautomator`);
  }
};

const LeadAutomator = (props) => {
  const [errorFetched, setErrorFetched] = useState(0);
  const [loading, setLoading] = useState(false);
  const userID = useSelector(selectUserId);
  const selectLeadAutomatorSection = useSelector(selectLeadAutomator)?.toJS();
  const { isFullAccount } = props;
  const planDetails = useSelector(selectPlanDetails)?.toJS();
  const isLegacyAccount = useSelector(isLegacyAccountPlan);
  const dashboard = useSelector(selectBillingDashboard);
  const usageSectionDetails = useSelector(selectUsageSection);
  const callCountForToday = useSelector(selectCallCountForToday);
  const callCountForTodayLoading = useSelector(selectCallCountForTodayLoading);
  const isLeadAutomatorLoading = useSelector(selectIsLeadAutomatorLoading);
  const isCallSettingsLoading = useSelector(selectCallSettingsLoading);
  const listManagementEnabled = useSelector(selectListManagementEnabled);
  const callSettings = useSelector(selectCallSettings);
  const profile     = useSelector(selectProfile).toJS();

  const currentPlanName = useSelector(selectUserPlan);

  const dispatch = useDispatch();
  const showToast = useToast();

  const goToLeadAutomator = () => {
    goToLeadAutomatorUpgrade({
      planDetails,
      isLegacyAccount,
      dispatch,
      history: props.history,
      leaveMessagePopup: props.leaveMessagePopup,
    });
  };

  useEffect(() => {
    if (userID) {
      getLeadAutomator();
    }
  }, [userID]);

  const getLeadAutomator = () => {
    setLoading(true);
    fetch(`${PRICING_UPDATE_URL}/ps/listautomator/users?userId=${userID}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": window.appAuthToken,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && data?.userId) {
          dispatch(loadLeadAutomatorSuccess(data));
          setErrorFetched(0);
        } else {
          setErrorFetched((prevCount) => prevCount + 1);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => setLoading(false));
  };

  const getCallCountForTodayInfo = () => {
    return {
      show:
        !(isCallSettingsLoading || callCountForTodayLoading) &&
        callCountForToday !== null && !!callSettings?.length,
      percentage:
        callCountForToday !== null
          ? ((callCountForToday / MAX_DAILY_CALLS) * 100).toFixed(2)
          : 0,
      remaining:
        callCountForToday !== null
          ? Math.max(0, MAX_DAILY_CALLS - callCountForToday)
          : 0,
      max: MAX_DAILY_CALLS,
      count: callCountForToday,
    };
  };

  const goToBilling = () => {
    props.history.push(`/accountnew/billing/0`);
  }

  const doNotCancel = () => {
    props.leaveMessagePopup({
      title: `Stop Cancellation of ${getConnectLabel()}`,
      message: <>By selecting <strong>Confirm</strong>, you will restore your $30/mo {getConnectLabel()} subscription. Please note: your main subscription plan will remain unaffected and continue to bill.</>,
      yesLable: "Confirm",
      cancelLable: "Cancel",
      yesRedirect: async () => {
        setLoading(true);
        const planMrcId = selectLeadAutomatorSection?.accountPlanMrcs?.find(
          (item) => item?.quantity === selectLeadAutomatorSection?.monitoredProperties,
        )?.id;
        const response = await revertUnsubscriptionLeadAutomator({
          userId: userID,
          planMrcId,
        });
        if (response.success) {
          showToast(`${getConnectLabel()} subscription restored successfully`, "success");
          // Restoring Connect changes the Investor Outreach card too (no longer "Connect cancelled too").
          refreshSubscriptionCards(dispatch, userID);
          getLeadAutomator();
        } else {
          setLoading(false);
          showToast(response?.error?.message || "Something went wrong, Please try again later", "error");
        }
      },
      bodyCss: css.doNotCancelBody,
      btnCss: css.doNotCancelBtn,
    });
  }
  // Example : 06/16/2026
  const isPastDate = (date) => {
    if (!date) return false;
    const [month, day, year] = String(date).split("/").map(Number);
    if (!month || !day || !year) return false;
    const target = new Date(year, month - 1, day);
    target.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return target < today;
  };

  const isLAPresent = !!selectLeadAutomatorSection?.accountPlanMrcs?.length;

  // ATAI-506: Essentials Annual + Investor Outreach is the fixed $1000/yr Connect + IO bundle (50k base
  // only), so Connect can't be changed while IO is subscribed (pending cancellation included). Once IO
  // has ended, Manage Plan comes back. Same cached entitlement the Investor Outreach card reads.
  const { entitled: isIOSubscribed } = useInvestorOutreachEntitlement(isPropStream() && !isFullAccount);
  const isEssentialsAnnual =
    currentPlanName === "Essentials" && !!planDetails?.accountEnrolledPlans?.[0]?.currentAnnual;
  const isConnectLockedByIO = isEssentialsAnnual && isIOSubscribed;

  const showManagePlanButton = !!(
    !(currentPlanName === "Pro" || currentPlanName === "Elite") &&
    !isConnectLockedByIO &&
    ((!planDetails?.trialAvailable && isLAPresent) || (planDetails?.trialAvailable && isLAPresent))
  );
  const showCardWithPropStreamConnect = !!(
    selectLeadAutomatorSection &&
    selectLeadAutomatorSection?.accountPlanMrcs &&
    selectLeadAutomatorSection?.accountPlanMrcs.length
  );
  const showCardWithoutPropStreamConnect = !showCardWithPropStreamConnect;
  const isError = !!(errorFetched > 0);
  const laSection = !!(
    selectLeadAutomatorSection &&
    selectLeadAutomatorSection?.accountPlanMrcs &&
    selectLeadAutomatorSection?.accountPlanMrcs.length
  );
  const isTrail = !planDetails?.usedLATrial;

  const trialTextTooltip = isTrail && "Try this feature free for 14 days!";

  const callCountInfo = getCallCountForTodayInfo();

  const status = dashboard?.psConnectStatus
  const trial = status == STATUS_MAP.TRIAL
  const trialCancelled = status == STATUS_MAP.TRIAL_CANCELLED
  const unsubscribed = status == STATUS_MAP.UNSUBSCRIBED
  const active = status == STATUS_MAP.ACTIVE
  const null_status = !status;

  const renewDate = dashboard?.leadAutomatorRenews
  const canceledDate = dashboard?.psConnectCancelDate
  const expiredDate = dashboard?.leadAutomatorExpires
  const endedDate = dashboard?.psConnectSubscriptionEndDate

  // Payment failed
  const paymentFailed = (active || null_status || trial) && endedDate && isPastDate(endedDate);
  const showLaBtn = (!isLAPresent) && (unsubscribed || trialCancelled) && !(currentPlanName === "Pro" || currentPlanName === "Elite");
  const laSubBtnText = isTrail
      ? "Start Free Trial"
      : "Subscribe Now";

  return (
    <div className={classNames(css.clsCard, css.clsLeadCard)}>
      <LoadablePanel loading={!currentPlanName || isLeadAutomatorLoading || loading}>
        <div
          className={classNames(
            css.clsCardHeader,
          )}
        >
          <div
            className={classNames(            
            css.d_flex,
            css.align_items_center,
            css.justify_content_between,
          )}
          >
            <h4 className={classNames(css.clsCardTitle)}>{getConnectLabel()}</h4>
            {showManagePlanButton && (
              <a
                href="javascript:;"
                className={classNames(css.clsCardTxt, css.text_primary)}
                onClick={goToLeadAutomator}
              >
                Manage Plan
              </a>
            )}
          </div>
        {
          trial && (<div className={classNames(css.info, css.infoMaxWidth)}>
            {" "}
            <SVG icon="info-circle" size={36} />{" "}
            <p>
              You are currently on a free trial for {getConnectLabel()}. Billing will begin on {renewDate}.
            </p>
          </div>)
        }
        {unsubscribed && isLAPresent && (
          <div className={classNames(css.info, css.infoDanger)}>
            {" "}
            <SVG icon="info-circle-white" size={36} />{" "}
            <p>
              Your {getConnectLabel()} subscription will end on {" "} {expiredDate}
            </p>{" "}
            <button className={css.ctaBtn} onClick={doNotCancel}>Do Not Cancel</button>{" "}
          </div>
        )}
        {(dashboard?.psConnectPendingPlanMrcId && !unsubscribed && renewDate && isLAPresent) && (
          <div className={classNames(css.info, css.infoDanger)}>
          {" "}
          <SVG icon="info-circle-white" size={36} />{" "}
            <p>
              Your {getConnectLabel()} subscription will be downgraded <br/> to  {
                selectLeadAutomatorSection?.planMrcs?.find(
                  plan => plan.id === dashboard?.psConnectPendingPlanMrcId
                )?.quantity?.toLocaleString()
              }{" "} monitored properties on {" "}
              {renewDate}.
            </p>
          </div>
        )}
        {paymentFailed && (
          <div className={classNames(css.info, css.infoDanger)}>
            <SVG icon="info-circle-white" size={36} />{" "}
            <p style={{maxWidth: "506px"}}>Your {getConnectLabel()} subscription ended on {endedDate} due to failed payment. Please call customer support at {profile.csPhone}.</p>
          </div>
        )}
        </div>
        {(unsubscribed && endedDate && !isLAPresent) && (
        <div className={classNames(css.info, css.infoMaxWidth)}>
          {" "}
          <SVG icon="info-circle" size={36} />{" "}
          <p>
            Your {getConnectLabel()} subscription has ended on{" "}
            {endedDate}
          </p>
        </div>
        )}
        {isError && (
          <ErrorDisplay
            errorFetched={errorFetched}
            onReload={getLeadAutomator}
          />
        )}
        {showCardWithoutPropStreamConnect && !isError && (
          <div className={css.clsCardBody}>
            <div className={css.clsLeadContentCard}>
              <div>
                <img src={isPropStream() ? propStreamConnect : connectLogo} alt={`${getConnectLabel()} logo`} />
              </div>

              <div className={css.clsLeadContentList}>
                <ul className={css.clsLeadListLeft}>
                  <li>
                    <SVG icon="pc-setting" size={22} width={27}  />
                    <p>Automate up to 50,000 Properties with Lead Automator</p>
                  </li>
                  <li>
                    <SVG icon="pc-search" size={22} />
                    <p>Free Skip Tracing</p>
                  </li>
                  <li>
                    <SVG icon="pc-phone" size={22} />
                    <p>Free Dialer Campaigns and Click-to-Dial</p>
                  </li>
                  <li>
                    <SVG icon="pc-stamp" size={22} />
                    <p>Discounted Direct Mail Pricing</p>
                  </li>
                </ul>
                <div className={css.clsLeadContenRight}>
                  <img src={propStreamConnectVisual} />
                </div>
              </div>

                <div className={classNames(css.btnContainer, "text-right")}>
                  {trialTextTooltip ? (
                    <Tooltip
                      content={trialTextTooltip}
                      xOffset={-90}
                      arrowOffset={200}
                      yOffset={-45}
                    >
                      <button
                        className={classNames(css.btn)}
                        onClick={goToLeadAutomator}
                      >
                        {laSubBtnText}
                      </button>
                    </Tooltip>
                  ) : (
                    <button
                      className={classNames(css.btn)}
                      onClick={goToLeadAutomator}
                    >
                      {laSubBtnText}
                    </button>
                  )}
                </div>
            </div>
          </div>
        )}

        {laSection && showCardWithPropStreamConnect && (
          <div className={classNames(css.clsLaSection)}>
            <div className={css.clsLaSubSection}>
              <p className={css.clsLaTitle}>Lead Automator</p>
              <div className={css.clsLeadBodyContent}>
                <CircularProgress
                  percentage={
                    100 -
                    parseFloat(
                      selectLeadAutomatorSection?.percentOfUsedProperties,
                    )
                  }
                  type="leadAutomatiornRemaining"
                  className={css.mr_5px}
                />
                <div className={css.clsLeadContentTxt}>
                  <h5
                    className={classNames(
                      css.clsLeadContent1,
                      css.monitoredRemaining,
                    )}
                  >
                    Monitored Properties Remaining
                  </h5>
                  <h4 className={css.clsLeadContent2}>
                    {numberFormat(
                      selectLeadAutomatorSection.monitoredPropertiesRemaining,
                    )}
                  </h4>
                  <div className={css.clsLeadContent3}>
                    <h6>
                      {numberFormat(
                        selectLeadAutomatorSection.monitoredPropertiesUsed,
                      )}
                    </h6>
                    <span className={css.clsBorder}></span>
                    <h6>
                      {selectLeadAutomatorSection.percentOfUsedProperties}
                    </h6>
                    <h6>Used</h6>
                  </div>
                </div>
              </div>
            </div>
            <div className={css.clsLaSubSection}>
              {callCountInfo.show && (
                <div
                  className={classNames(
                    css.clsLeadBodyContent,
                    css.rightDivider,
                  )}
                >
                  <CircularProgress
                    percentage={100 - Number(callCountInfo?.percentage)}
                    className={css.mr_5px}
                    type="callremaining"
                  />
                  <div className={css.clsLeadContentTxt}>
                    <h5
                      className={classNames(
                        css.clsLeadContent1,
                        css.monitoredRemaining,
                      )}
                    >
                      <span>Daily Calls Remaining</span>
                    </h5>

                    <h4 className={css.clsLeadContent2}>
                      {callCountInfo.remaining}
                    </h4>
                    <div className={css.clsLeadContent3}>
                      <h6>{callCountInfo.count}</h6>
                      <span className={css.clsBorder}></span>
                      <h6>{callCountInfo?.percentage}%</h6>
                      <h6>Used</h6>
                    </div>
                  </div>
                </div>
              )}
              <div className={css.clsLeadBodyContent}>
                <CircularProgress
                  percentage={
                    100 - Number(usageSectionDetails?.skipTracesPercentage)
                  }
                  type="freeskiptraceremaining"
                  className={css.mr_5px}
                />
                <div className={css.clsLeadContentTxt}>
                  <h5
                    className={classNames(
                      css.clsLeadContent1,
                      css.monitoredRemaining,
                    )}
                  >
                    <span>Skip Traces remaining</span>
                  </h5>

                  <h4 className={css.clsLeadContent2}>
                    {usageSectionDetails?.skipTracesRemaining?.toLocaleString()}
                  </h4>
                  <div className={css.clsLeadContent3}>
                    <h6>
                      {usageSectionDetails?.skipTracesUsed?.toLocaleString()}
                    </h6>
                    <span className={css.clsBorder}></span>
                    <h6>{usageSectionDetails?.skipTracesPercentage}%</h6>
                    <h6>Used</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </LoadablePanel>
    </div>
  );
};

export default withRouter(
  connect(null, {
    leaveMessagePopup: LeaveMessagePopup.open,
  })(LeadAutomator),
);